import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View, { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import buttons from 'src/lib/ui/config/buttons';
import { MovieClip, animate } from '@play-co/timestep-core/ui';
import { AssetGroup } from '@play-co/timestep-core';
import { Feature } from 'src/replicant/analytics';
import i18n from 'src/lib/i18n/i18n';
import { getScreenDimensions, waitForItPromise } from 'src/lib/utils';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import sounds from 'src/lib/sounds';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import { CardsRibbon } from '../components/CardsRibbon';
import { CardsShine } from '../components/CardsShine';
import { CardId } from 'src/replicant/cards/cards.ruleset';
import { CardsPack } from '../components/CardsPack';
import gsap from 'gsap';
import { CardsCongratulations } from '../components/CardsCongratulations';
import { Card, CardState } from '../components/Card';

type CloseHandler = () => void;

export default class PopupCardsPackReward extends PopupBasicLazy {
  private congratulations: CardsCongratulations;
  private claimButton: ButtonScaleViewWithText;
  private shine: CardsShine;
  private ribbon: CardsRibbon;
  private confetti: MovieClip;
  private tapToOpen: LangBitmapFontTextView;
  private pack: CardsPack;
  private cards: Card[] = [];
  private cardsContainer: FlexBoxView;
  private tapArea: View;
  private close: CloseHandler;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = opts.superview.style.width;
    const height = getScreenDimensions().height;

    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      width,
      height,
      close: () => opts.close(),
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.POPUP_PACK_REWARD,
      },
      zIndex: 10000,
    });

    // This color is the same as backgroundDarker in uiConfig.popups, but less transparent
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.9)',
    });

    this.close = opts.close;

    this.congratulations = new CardsCongratulations({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 200,
      centerOnOrigin: true,
    });

    this.ribbon = new CardsRibbon({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 290,
      centerOnOrigin: true,
      scale: 0,
    });

    this.buttonClose.updateOpts({
      visible: false,
    });

    this.shine = new CardsShine({
      superview: this.box,
      x: width / 2,
      y: height / 2,
      radius: 350,
      opacity: 0.7,
      scale: 0,
    });

    this.cardsContainer = new FlexBoxView({
      superview: this.box,
      x: width / 2,
      y: height / 2,
      width: this.box.style.width,
      height: 600,
      centerOnOrigin: true,
      flex: {
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        flexWrap: 'wrap',
        alignItems: 'center',
        alignContent: 'center',
      },
    });

    this.pack = new CardsPack({
      superview: this.box,
      x: width / 2,
      y: height / 2,
      centerOnOrigin: true,
      scale: 0,
    });

    this.confetti = new MovieClip({
      superview: this.box,
      url: 'assets/ui/match2/movieclips/level_complete_stars',
      x: width / 2,
      y: height / 2 + 200,
    });

    this.claimButton = new ButtonScaleViewWithText({
      superview: this.box,
      ...buttons.secondary,
      width: 317,
      height: 95,
      fontSize: 40,
      labelOffsetY: -2,
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: this.box.style.height - 200,
      font: 'Body',
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.BUTTON_START,
      },
      localeText: () => i18n('cards.packRewardPopup.claim'),
      onClick: async () => this.claim(),
    });

    this.tapArea = new View({
      superview: this.box,
      width,
      height,
      visible: false,
    });

    this.tapToOpen = new LangBitmapFontTextView({
      superview: this.tapArea,
      x: width / 2,
      y: height - 200,
      width: 500,
      height: 100,
      align: 'center',
      verticalAlign: 'center',
      size: 50,
      font: 'Title',
      color: '#FFFFFF',
      wordWrap: true,
      localeText: () => i18n('cards.packRewardPopup.tapToOpen'),
      centerOnOrigin: true,
      scale: 0,
    });
  }

  public getAssetsGroup() {
    return new AssetGroup(
      AssetGroup.constructURLs([
        'assets/ui/cards/common',
        'assets/ui/cards/popupCardsPackReward',
      ]),
    );
  }

  public async init(args: { tier: number; cards: CardState[] }) {
    super.init({});

    this.pack.setup({ tier: args.tier });

    this.ribbon.setText(
      i18n('cards.packRewardPopup.youGot', { num: args.cards.length }),
    );

    this.claimButton.hide();

    this.cards = args.cards.map((cardState, i) => {
      const card = new Card({ superview: this.cardsContainer });
      getFlexNode(card).updateStyle({
        marginVertical: 10,
      });
      card.setState(cardState);
      card.flip(true, 0);
      return card;
    });

    this.cardsContainer.updateOpts({
      opacity: 0,
    });
  }

  public async fadeIn() {
    this.overlay.show();
    this.attachRoot();
    this.box.hide();
    this.bg.updateOpts({ opacity: 0 });
    await this.loadAssets();
    this.overlay.hide();
    this.box.show();
    await gsap.to(this.bg.style, {
      opacity: 1,
      duration: 0.2,
      ease: 'linear',
    });
    this.presentPack();
  }

  public async fadeOut() {
    this.overlay.show();
    this.shine.stopAutoUpdate();
    animate(this.box)
      .clear()
      .wait(0)
      .then({ opacity: 0 }, 200, animate.easeOut)
      .toPromise()
      .then(() => {
        this.detachRoot();
        this.overlay.hide();
      });
  }

  private async presentPack() {
    sounds.sfx.play('tada');

    await this.congratulations.showAnimated();

    await gsap.to(this.pack.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
    });

    this.shine.autoUpdate();
    await gsap.to(this.shine.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
    });

    this.tapArea.show();
    this.tapArea.onInputStart = () => this.openPack();
    await gsap.to(this.tapToOpen.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
    });
  }

  private async openPack() {
    this.tapArea.hide();
    sounds.sfx.play('gachaItemDrop');

    this.ribbon.updateOpts({
      visible: true,
      scale: 0,
    });

    await gsap.to(this.pack.style, {
      offsetY: 300,
      duration: 0.3,
      ease: 'quad.inOut',
    });

    this.confetti.play(`staranim_confetti`);
    sounds.sfx.play('applause');
    this.pack.open();

    gsap.to(this.ribbon.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
      delay: 0.5,
    });

    await this.revealCards();

    this.claimButton.updateOpts({
      scale: 0,
      visible: true,
    });

    gsap.to(this.claimButton.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
    });
  }

  private async revealCards() {
    gsap.to(this.cardsContainer.style, {
      opacity: 1,
      duration: 0.2,
      ease: 'quad.out',
    });

    const cardsProps: ViewOpts[] = [];
    const baseX = this.box.style.width / 2 - 90;
    const baseY = 600;
    const spacing = 30 / this.cards.length;

    this.cards.forEach((card, i) => {
      // Save X & Y positions to animate back to them later
      const x = card.style.x;
      const y = card.style.y;
      const scale = card.style.scale;
      const r = card.style.r;
      cardsProps.push({ x, y, r, scale });
      // Move card to the bottom of the screen
      card.updateOpts({
        x: baseX + spacing * i,
        y: baseY + spacing * i,
        r: 0.05,
        scale: 0.9,
        zIndex: -i, // The last one should be in the back
      });
    });

    await waitForItPromise(1000);

    for (const card of this.cards) {
      const i = this.cards.indexOf(card);
      const props = cardsProps.shift();
      const x = props?.x ?? 0;
      const y = props?.y ?? 0;
      const r = props?.r ?? 0;
      const scale = props?.scale ?? 1;
      card.updateOpts({ zIndex: i });
      gsap.to(card.style, {
        y,
        r,
        scale,
        duration: 0.4,
        ease: 'quad.out',
      });
      await gsap.to(card.style, {
        x,
        duration: 0.3,
        delay: 0.1,
        ease: 'quad.inOut',
      });
      await card.flip(false, 300);
    }

    await gsap.to(this.pack.style, {
      offsetY: 600,
      duration: 0.3,
      ease: 'back.in',
    });

    this.pack.hide();
  }

  private async claim() {
    sounds.sfx.play('win');
    this.claimButton.hide();
    this.close();
  }
}
